/* Bulk serial scanner.
 *
 * The shape of this screen is set by one requirement: a supplier at a warehouse
 * works down a pallet, back to back. So the camera stays open, the list grows
 * under it, and nothing dismisses itself after a read. The feedback line is the
 * only thing that changes per scan, and it is one line so it can be read at
 * arm's length.
 *
 * Manual entry is not behind a button that appears after a failure. It is a text
 * field that is on the screen the whole time, because in a warehouse with bad
 * light, damaged labels and gloved hands it is not a fallback, it is the second
 * primary path — and serials come back most reliably at pre-flight (99.97%)
 * precisely where the operator has time to type.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import './capture.css';
import { CameraGlyph } from './CameraGlyph';
import {
  browserCameraPorts,
  isRetryable,
  openCamera,
  stopStream,
  type CameraFailure,
  type CameraPorts,
  type TorchControl,
} from './cameraStream';
import { loadDecoder, type BarcodeDecoder } from './barcodeDecoder';
import {
  browserLoopPorts,
  newCaptureId,
  startScanLoop,
  type ScanLoopHandle,
  type ScanLoopOptions,
} from './scanLoop';
import { emptySession, offer, removeCapture, sessionCounts } from './scanSession';
import {
  DEFAULT_SERIAL_RULES,
  type RejectReason,
  type ScanEvent,
  type ScanSessionState,
  type SerialCapture,
  type SerialRules,
} from './types';

/** Everything the scanner needs from the outside world. Injectable for tests. */
export interface ScannerDeps {
  cameraPorts: CameraPorts;
  /** Resolves the decoder. Defaults to the lazily-loaded bundled WASM. */
  loadDecoder(): Promise<BarcodeDecoder>;
  now(): number;
  newId(): string;
}

export interface SerialScannerProps {
  /** Heading, e.g. `Scan module serials`. */
  title?: string;
  /**
   * Where this is happening and why, e.g. `Kiosk AHL-8842 · Installation`. The
   * intervention context is what makes the count meaningful.
   */
  context?: string;
  /** Modules the intervention expects, if known. Drives the progress line. */
  expectedCount?: number;
  /** Serials already on this submission, so re-scans are caught immediately. */
  initialCaptures?: readonly SerialCapture[];
  rules?: SerialRules;
  loopOptions?: Partial<ScanLoopOptions>;
  /** Fires on every change, so a form can mirror the list into its own state. */
  onChange?: (captures: readonly SerialCapture[]) => void;
  /** Technician is done. Receives the final list in capture order. */
  onDone: (captures: readonly SerialCapture[]) => void;
  onCancel?: () => void;
  /** Test seam. Anything omitted falls back to the real browser implementation. */
  deps?: Partial<ScannerDeps>;
}

type CameraState =
  | { phase: 'idle' }
  | { phase: 'starting' }
  | { phase: 'live' }
  | { phase: 'failed'; failure: CameraFailure };

function describeEvent(event: ScanEvent): { text: string; tone: 'ok' | 'dup' | 'bad' } {
  switch (event.kind) {
    case 'accepted':
      return { text: `Added ${event.capture.serial}`, tone: 'ok' };
    case 'duplicate':
      return {
        text: `${event.serial} is already in this list${
          event.count > 1 ? ` (seen ${event.count} more times)` : ''
        }`,
        tone: 'dup',
      };
    case 'rejected':
      return { text: `${rejectText(event.reason)} — type it instead`, tone: 'bad' };
    default:
      return { text: '', tone: 'ok' };
  }
}

function rejectText(reason: RejectReason): string {
  switch (reason) {
    case 'empty':
      return 'Nothing read';
    case 'too-short':
      return 'That is too short to be a serial';
    case 'too-long':
      return 'That is too long to be a serial';
    case 'charset':
      return 'That has characters a serial cannot contain';
    case 'checksum':
      return 'That read failed its checksum';
    default:
      return 'That could not be used';
  }
}

export function SerialScanner({
  title = 'Scan module serials',
  context,
  expectedCount,
  initialCaptures,
  rules = DEFAULT_SERIAL_RULES,
  loopOptions,
  onChange,
  onDone,
  onCancel,
  deps,
}: SerialScannerProps): JSX.Element {
  /*
   * Per-instance id for the manual-entry input. Two modules on one form step means two scanner
   * sheets, and a hardcoded id silently mis-associates both labels. See the note at the input.
   */
  const manualInputId = useId();

  const resolved = useMemo<ScannerDeps>(
    () => ({
      cameraPorts: deps?.cameraPorts ?? browserCameraPorts(),
      loadDecoder: deps?.loadDecoder ?? loadDecoder,
      now: deps?.now ?? (() => Date.now()),
      newId: deps?.newId ?? newCaptureId,
    }),
    [deps],
  );

  const seeded = useMemo<ScanSessionState>(() => {
    let s = emptySession();
    for (const c of initialCaptures ?? []) {
      s = offer(s, { raw: c.raw, format: c.format, method: c.method, at: c.at, id: c.id }, rules);
    }
    return s;
  }, [initialCaptures, rules]);

  const [session, setSession] = useState<ScanSessionState>(seeded);
  const [camera, setCamera] = useState<CameraState>({ phase: 'idle' });
  const [torch, setTorch] = useState<TorchControl | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [feedback, setFeedback] = useState<{ text: string; tone: string } | null>(null);
  const [manual, setManual] = useState('');
  const [decodeErrors, setDecodeErrors] = useState(0);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const loopRef = useRef<ScanLoopHandle | null>(null);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  useEffect(() => {
    onChange?.(session.captures);
  }, [session.captures, onChange]);

  const teardown = useCallback(() => {
    loopRef.current?.stop();
    loopRef.current = null;
    stopStream(streamRef.current);
    streamRef.current = null;
    setTorch(null);
    setTorchOn(false);
  }, []);

  useEffect(() => teardown, [teardown]);

  const start = useCallback(async () => {
    setCamera({ phase: 'starting' });
    // Permission is asked for here, at the point of use, with the reason on
    // screen — not on app load. On iOS Safari a denial is expensive to undo.
    const opened = await openCamera(resolved.cameraPorts);
    if (!opened.ok) {
      setCamera({ phase: 'failed', failure: opened.failure });
      return;
    }
    streamRef.current = opened.stream;
    setTorch(opened.torch);

    const video = videoRef.current;
    if (video) {
      video.srcObject = opened.stream;
      try {
        await video.play();
      } catch {
        // Autoplay refusal is not fatal; the first frame grab simply returns
        // null until the element is playing.
      }
    }

    let decoder: BarcodeDecoder;
    try {
      decoder = await resolved.loadDecoder();
    } catch (err) {
      teardown();
      setCamera({
        phase: 'failed',
        failure: {
          kind: 'unknown',
          name: 'DecoderLoadError',
          message: `The barcode decoder could not be loaded (${
            err instanceof Error ? err.message : String(err)
          }). You can still type serials.`,
        },
      });
      return;
    }

    if (!video) {
      teardown();
      setCamera({
        phase: 'failed',
        failure: {
          kind: 'unknown',
          name: 'NoVideoElement',
          message: 'The viewfinder is not ready. Type the serials, or try again.',
        },
      });
      return;
    }

    setCamera({ phase: 'live' });
    const ports = {
      ...browserLoopPorts(video, (frame) => decoder.decode(frame)),
      now: resolved.now,
      newId: resolved.newId,
    };
    loopRef.current = startScanLoop(
      sessionRef.current,
      ports,
      {
        onState: setSession,
        onEvent: (event) => setFeedback(describeEvent(event)),
        onDecodeError: () => setDecodeErrors((n) => n + 1),
      },
      { ...{ intervalMs: 100, cooldownMs: 1200, rules }, ...loopOptions },
    );
  }, [resolved, rules, loopOptions, teardown]);

  const submitManual = useCallback(() => {
    const raw = manual;
    const live = loopRef.current;
    const event = live
      ? live.submitManual(raw)
      : (() => {
          const next = offer(
            sessionRef.current,
            { raw, method: 'manual', at: resolved.now(), id: resolved.newId() },
            rules,
          );
          setSession(next);
          return next.lastEvent;
        })();
    if (event) setFeedback(describeEvent(event));
    if (event?.kind === 'accepted') setManual('');
  }, [manual, resolved, rules]);

  const drop = useCallback(
    (id: string) => {
      const next = removeCapture(sessionRef.current, id);
      setSession(next);
      setFeedback(null);
    },
    [],
  );

  const counts = sessionCounts(session);
  const remaining =
    expectedCount === undefined ? null : Math.max(0, expectedCount - counts.unique);

  return (
    <section className="cap-sheet" aria-label={title}>
      <header>
        <h2 className="section-title">{title}</h2>
        {context && <p className="muted">{context}</p>}
      </header>

      <ul className="counts">
        <li>
          <strong>{counts.unique}</strong> captured
        </li>
        <li>
          <strong>{counts.duplicates}</strong> duplicate{counts.duplicates === 1 ? '' : 's'}{' '}
          skipped
        </li>
        <li>
          <strong>{counts.typed}</strong> typed
        </li>
        {remaining !== null && (
          <li>
            <strong>{remaining}</strong> still expected
          </li>
        )}
      </ul>

      {/* The <video> is mounted unconditionally, and hidden rather than
          unmounted when the camera is off. If it were mounted in response to a
          state change, `videoRef.current` would still be null on the tick where
          `start()` needs it, and whether it worked would depend on how React
          happened to batch — a scanner that starts only sometimes. */}
      <div className="cap-viewfinder">
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          style={camera.phase === 'live' ? undefined : { display: 'none' }}
        />
        {camera.phase === 'live' && <span className="cap-reticle" aria-hidden="true" />}
        <p className="cap-hint">
          {camera.phase === 'live'
            ? 'Hold a label in the frame. Keep going — the list below grows as you scan.'
            : camera.phase === 'starting'
              ? 'Starting the camera…'
              : camera.phase === 'idle'
                ? 'The camera is off. You can scan, or type serials below.'
                : camera.failure.message}
        </p>
      </div>

      <div className="cap-row">
        {camera.phase === 'idle' && (
          /*
            `cap-scan` + the glyph: the visibly primary action of the sheet while the camera is off
            (field feedback 5). The camera deliberately does NOT auto-start when the sheet opens —
            `start()` is where permission is requested, at the point of use, because an iOS Safari
            denial is expensive to undo — so this button is the whole scan affordance and it must not
            render in the same weight as "Cancel". Typing stays on screen below it as the second
            primary path; emphasis here is not demotion there.
          */
          <button type="button" className="button cap-scan" onClick={() => void start()}>
            <CameraGlyph />
            Scan with camera
          </button>
        )}
        {camera.phase === 'live' && (
          <>
            <button type="button" className="button" onClick={teardown}>
              Stop camera
            </button>
            {torch && (
              /*
               * THE NAME IS CONSTANT; `aria-pressed` CARRIES THE STATE.
               *
               * This was `aria-pressed={torchOn}` with a label that flipped to "Light off" when the
               * torch was on, so a screen reader announced "Light off, pressed" — the label saying
               * one thing and the state saying its opposite. Whichever the technician believed, half
               * the announcement was wrong.
               *
               * So the accessible name is now always "Light". The ON/OFF word is `aria-hidden`
               * because `aria-pressed` already says it — but it stays on screen, because a toggle
               * whose only sighted state signal is a border colour is colour-as-the-only-signal in
               * direct sun, and this button controls the light a technician is reading a plate by.
               * The visible label still STARTS with the accessible name, so voice control ("tap
               * Light") still matches it (WCAG 2.5.3).
               */
              <button
                type="button"
                className="button cap-torch"
                aria-pressed={torchOn}
                onClick={() => {
                  const next = !torchOn;
                  setTorchOn(next);
                  void (next ? torch.on() : torch.off()).catch(() => setTorchOn(!next));
                }}
              >
                Light
                <span className="cap-torch__state" aria-hidden="true">
                  {torchOn ? 'ON' : 'OFF'}
                </span>
              </button>
            )}
          </>
        )}
        {camera.phase === 'failed' && isRetryable(camera.failure) && (
          <button type="button" className="button" onClick={() => void start()}>
            Try the camera again
          </button>
        )}
      </div>

      <p
        className={`cap-feedback${feedback ? ` cap-feedback--${feedback.tone}` : ''}`}
        role="status"
        aria-live="polite"
      >
        {feedback?.text ?? 'Nothing scanned yet.'}
      </p>

      {/*
        Always present, never gated behind a failure.

        The id is PER INSTANCE (`useId`), not the literal `cap-manual` it used to be. A form step
        with two or more modules renders two scanner sheets, and with a hardcoded id the first input
        was associated with BOTH `<label for>` elements — named twice over — while the second was
        associated with NEITHER, so its only accessible name came from the placeholder fallback.

        ⚠️ **axe reported ZERO violations for this.** `duplicate-id-active` is deprecated out of
        axe 4.13's WCAG tag set, and the `label` rule passed on that placeholder fallback. So a green
        accessibility run is not evidence of a working label — this was found by asserting the
        association directly (`getAllByLabelText` returned 1 of 2), not by the tooling.
      */}
      <div className="cap-row">
        <label className="cap-sr" htmlFor={manualInputId}>
          Serial as printed on the module plate
        </label>
        <input
          id={manualInputId}
          className="cap-input"
          value={manual}
          onChange={(e) => setManual(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitManual();
            }
          }}
          placeholder="Type a serial"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
        />
        <button type="button" className="button" onClick={submitManual} disabled={manual === ''}>
          Add
        </button>
      </div>

      {/*
       * NEWEST FIRST, IN THE DOM — not `flex-direction: column-reverse`.
       * ===============================================================
       * `session.captures` is capture order, oldest first, and after ~15 scans down a pallet the read
       * a technician just took was below the fold while the first one of the day sat at the top.
       *
       * Reversing it in CSS would fix the picture and break WCAG 1.3.2: the reading order a screen
       * reader, a switch-control user and a `Tab` key follow is DOM order, so visually the newest
       * would be first and audibly it would be last — and "Remove" buttons that walk the opposite way
       * to the list they are attached to is a way to delete the wrong serial.
       *
       * So the ARRAY is reversed and each row keeps its ORIGINAL index. `i + 1` is the capture
       * number, i.e. the "12" in "twelfth module I scanned"; it is not the row's position on screen
       * and must not be renumbered, because the count is what a technician reconciles against the
       * pallet. `.reverse()` runs on a fresh array from `map`, so `session.captures` is untouched.
       */}
      {session.captures.length > 0 && (
        <ul className="cap-list" aria-label="Captured serials, newest first">
          {session.captures
            .map((c, i) => ({ capture: c, index: i }))
            .reverse()
            .map(({ capture: c, index: i }) => (
              <li key={c.id}>
                <span className="cap-list__index">{i + 1}</span>
                <span className="cap-list__serial">{c.serial}</span>
                <span className="cap-list__method">
                  {c.method === 'scan' ? (c.format ?? 'scan') : 'typed'}
                </span>
                <button
                  type="button"
                  className="button"
                  onClick={() => drop(c.id)}
                  aria-label={`Remove ${c.serial}`}
                >
                  Remove
                </button>
              </li>
            ))}
        </ul>
      )}

      {decodeErrors > 0 && (
        <p className="muted">
          {decodeErrors} frame{decodeErrors === 1 ? '' : 's'} could not be decoded. That is normal
          while moving; if nothing is landing at all, type the serials.
        </p>
      )}

      <div className="cap-row">
        {/*
          `cap-primary` names the commit action on the element itself. `capture.css` previously found it
          with `.cap-sheet > .cap-row:last-child > .button:first-child` — emphasis by DOM position,
          which silently transfers to whatever button happens to move into that slot. Adding "Cancel"
          first, or a row after this one, would have moved the accent onto the wrong control with no
          test and no visual diff to catch it.
        */}
        <button
          type="button"
          className="button cap-primary"
          onClick={() => {
            teardown();
            onDone(session.captures);
          }}
        >
          Done{counts.unique > 0 ? ` — ${counts.unique} serial${counts.unique === 1 ? '' : 's'}` : ''}
        </button>
        {onCancel && (
          <button
            type="button"
            className="button"
            onClick={() => {
              teardown();
              onCancel();
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </section>
  );
}
